import { customAuth } from "../standardAuthPatterns";
import logo from "./logo.svg";
import { defineProduct, productVariant } from "../types";
import { urlToPattern } from "../urlToPattern";

type GithubConfig = { githubHost?: string };

const resolveGithubHost = ({ githubHost }: GithubConfig) => {
  const host = githubHost?.trim().toLowerCase() || "github.com";
  const url = new URL(`https://${host}`);
  if (url.hostname !== host || host.endsWith(".")) {
    throw new Error("Invalid GitHub host");
  }
  return host;
};

const githubUrls = (configuration: GithubConfig) => {
  const host = resolveGithubHost(configuration);
  const web = `https://${host}`;
  return {
    host,
    web,
    api: host === "github.com" ? "https://api.github.com" : `${web}/api/v3`,
  };
};

const githubEnterpriseUrls = (configuration: GithubConfig) => {
  const urls = githubUrls(configuration);
  if (configuration.githubHost !== undefined && urls.host === "github.com") {
    throw new Error("Invalid GitHub Enterprise Server host");
  }
  return urls;
};

const githubUrlPattern = (configuration: GithubConfig) => {
  const host = resolveGithubHost(configuration);
  return host === "github.com"
    ? "https://(?:api\\.)?github\\.com/.*"
    : urlToPattern(`https://${host}`);
};

const githubHostStep = {
  title: "Enter your GitHub host",
  items: [
    {
      kind: "hostname",
      label: "GitHub host",
      placeholder: "github.com",
      defaultValue: "github.com",
      helperText:
        "github.com, or the hostname of your HTTPS-enabled GitHub Enterprise Server instance.",
      target: { kind: "configuration", field: "githubHost" },
    },
  ],
} as const;

const githubEnterpriseHostStep = {
  title: "Enter your GitHub Enterprise Server host",
  items: [
    {
      kind: "hostname",
      label: "GitHub host",
      placeholder: "github.example.com",
      required: true,
      helperText:
        "The hostname of your HTTPS-enabled GitHub Enterprise Server instance.",
      target: { kind: "configuration", field: "githubHost" },
    },
  ],
} as const;

const githubAppCredentialItems = [
  {
    kind: "markdown",
    content:
      "Enter the app ID and private key from your GitHub App’s settings. Find the installation ID in an installation webhook payload or through [GitHub’s REST API](https://docs.github.com/apps/creating-github-apps/authenticating-with-a-github-app/authenticating-as-a-github-app-installation).",
  },
  {
    kind: "input",
    label: "App ID",
    placeholder: "123456",
    required: true,
    target: { kind: "configuration", field: "appId" },
  },
  {
    kind: "file",
    label: "Private key (.pem)",
    accept: ".pem",
    helperText: "The .pem file downloaded from the app’s settings.",
    target: { kind: "secrets", field: "privateKey" },
  },
  {
    kind: "input",
    label: "Installation ID",
    placeholder: "12345678",
    required: true,
    target: { kind: "configuration", field: "installationId" },
  },
] as const;

const KEYWORDS =
  "repositories pull requests issues actions ci cd source control ghes enterprise server";

export const github = defineProduct({
  name: "GitHub",
  tagLine: "Code hosting and collaboration",
  logo,
  tags: ["featured", "engineering"],
  variants: [
    productVariant({
      identifier: "github",
      authLabel: "User OAuth",
      keywords: KEYWORDS,
      protocol: "http",
      defaultUrlPattern: githubUrlPattern,
      auth: customAuth("github"),
      steps: [
        githubHostStep,
        {
          title: "Begin creating a GitHub OAuth app",
          items: [
            {
              kind: "markdown",
              content:
                "Open **Settings → Developer settings → OAuth Apps** in GitHub, then click **New OAuth App** or, if this is your first OAuth app, **Register a new application**.",
            },
          ],
        },
        {
          title: "Fill out the details",
          items: [
            {
              kind: "markdown",
              content:
                "Copy and paste the following information into the form:",
            },
            {
              kind: "copyable",
              label: "Application name",
              value: "3B",
            },
            {
              kind: "copyable",
              label: "Homepage URL",
              value: "{UI_URL}",
            },
            {
              kind: "copyable",
              label: "Authorization callback URL",
              value: "{API_URL}/connected-apps/oauth/github/callback",
            },
          ],
        },
        {
          title: "Save",
          items: [
            {
              kind: "markdown",
              content:
                "Click **'Register application'** to submit the form and save.",
            },
          ],
        },
        {
          title: "Generate a client secret",
          items: [
            {
              kind: "markdown",
              content:
                "On your newly created OAuth app, click **Generate a new client secret**.",
            },
          ],
        },
        {
          title: "Input your client ID and generated secret",
          items: [
            {
              kind: "input",
              label: "Client ID",
              placeholder: "Paste your Client ID",
              required: true,
              target: { kind: "oauthConnection", field: "clientId" },
            },
            {
              kind: "input",
              label: "Client secret",
              placeholder: "Paste your Client secret",
              required: true,
              target: { kind: "oauthConnection", field: "clientSecret" },
            },
          ],
        },
        {
          title: "Define scopes for this connection",
          items: [
            {
              kind: "markdown",
              content: (configuration) => {
                const docs =
                  githubUrls(configuration).host === "github.com"
                    ? "https://docs.github.com/en/apps/oauth-apps/building-oauth-apps/scopes-for-oauth-apps"
                    : "https://docs.github.com/en/enterprise-server@latest/apps/oauth-apps/building-oauth-apps/scopes-for-oauth-apps";
                return `Add [scopes](${docs}#available-scopes) in a space separated list below.`;
              },
            },
            {
              kind: "input",
              label: "Scopes (optional)",
              defaultValue: "repo workflow",
              helperText: "Space separated list of desired scopes.",
              target: { kind: "oauthConnection", field: "scopes" },
            },
          ],
        },
        {
          title: "Authorize your account in GitHub",
          items: [
            {
              kind: "markdown",
              content:
                "Finally, we’ll authorize your desired GitHub account for this OAuth app so it can be used in steps in 3B.",
            },
            {
              kind: "oauth",
              dependsOn: [
                { kind: "oauthConnection", field: "clientId" },
                { kind: "oauthConnection", field: "clientSecret" },
              ],
            },
          ],
        },
      ],
      oauth: {
        grantType: () => "authorization_code",
        authorizationEndpoint: (configuration) =>
          `${githubUrls(configuration).web}/login/oauth/authorize`,
        tokenEndpoint: (configuration) =>
          `${githubUrls(configuration).web}/login/oauth/access_token`,
        tokenEndpointAuthMethod: () => "client_secret_post",
        clientCredentialsFrom: "db",
        scopes: () => ["repo", "workflow"],
        authorizationParameters: () => ({
          allow_signup: "false",
        }),
      },
      connectionTest: {
        protocol: "http",
        request: (configuration) => `${githubUrls(configuration).api}/user`,
      },
    }),
    productVariant({
      identifier: "githubApp",
      authLabel: "GitHub App",
      keywords: KEYWORDS,
      protocol: "http",
      defaultUrlPattern: () => "https://(?:api\\.)?github\\.com/.*",
      auth: customAuth("githubApp"),
      steps: [
        {
          title: "Create and install the GitHub App",
          items: [
            {
              kind: "markdown",
              content:
                "We’ll open GitHub to create a preconfigured app. GitHub uses the account currently signed in, so switch accounts if needed. Review the app, then choose the repositories to install it on.",
            },
            {
              kind: "input",
              label: "GitHub organization login (optional)",
              placeholder: "my-org",
              helperText:
                "Enter the organization’s login, not a username. Leave blank for a personal account.",
              target: { kind: "configuration", field: "organizationLogin" },
            },
            {
              kind: "githubAppManifest",
            },
          ],
        },
        {
          title: "Or configure an existing app manually",
          items: githubAppCredentialItems,
        },
      ],
      connectionTest: {
        protocol: "http",
        request: () =>
          "https://api.github.com/installation/repositories?per_page=1",
      },
      usageHint: () =>
        "This connection authenticates as a GitHub App installation, not as a user. Use the GitHub REST API at https://api.github.com.",
    }),
    productVariant({
      identifier: "githubEnterpriseApp",
      authLabel: "GitHub App (Enterprise Server)",
      keywords: KEYWORDS,
      protocol: "http",
      defaultUrlPattern: (configuration) =>
        urlToPattern(`${githubEnterpriseUrls(configuration).web}/api`),
      auth: customAuth("githubApp"),
      steps: [
        githubEnterpriseHostStep,
        {
          title: "Configure an existing GitHub App",
          items: githubAppCredentialItems,
        },
      ],
      connectionTest: {
        protocol: "http",
        request: (configuration) =>
          `${githubEnterpriseUrls(configuration).api}/installation/repositories?per_page=1`,
      },
      usageHint: (configuration) =>
        `This connector authenticates as a GitHub App installation for API requests to ${githubEnterpriseUrls(configuration).api}.`,
    }),
    productVariant({
      identifier: "githubToken",
      authLabel: "Personal access token",
      keywords: KEYWORDS,
      protocol: "http",
      defaultUrlPattern: githubUrlPattern,
      auth: customAuth("githubToken"),
      steps: [
        githubHostStep,
        {
          title: "Create a personal access token",
          items: [
            {
              kind: "markdown",
              content:
                "Open **Settings → Developer settings → Personal access tokens** in GitHub, then select **Generate new token**. Choose the repositories and permissions your workflows need, then select **Generate token**.",
            },
          ],
        },
        {
          title: "Enter your token",
          items: [
            {
              kind: "input",
              label: "Personal access token",
              placeholder: "github_pat_…",
              required: true,
              target: { kind: "secrets", field: "token" },
            },
          ],
        },
      ],
      connectionTest: {
        protocol: "http",
        request: (configuration) => `${githubUrls(configuration).api}/user`,
      },
      usageHint: (configuration) =>
        `This connection authenticates with a personal access token. Use the GitHub REST API at ${githubUrls(configuration).api}.`,
    }),
  ],
});
